import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useYouTube } from "@/context/YouTubeContext";
import { Reveal } from "@/components/Reveal";
import { BrandLogo } from "@/components/BrandLogo";
import { CHANNEL_SNAPSHOT } from "@/data/content";

const Counter = ({ value, suffix, decimals = 0, testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState((0).toFixed(decimals));

  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v.toFixed(decimals)),
    });
    return () => controls.stop();
  }, [inView, value, decimals]);

  return (
    <span ref={ref} data-testid={testId} className="font-display text-5xl font-extrabold tracking-tight text-cream md:text-7xl">
      {display}
      <span className="text-ember">{suffix}</span>
    </span>
  );
};

// Turn a raw count into counter-friendly parts, e.g. 919000 -> 919 K+, 1097945 -> 1.1 M+
const compact = (n) => {
  if (n >= 1_000_000_000) return { value: n / 1_000_000_000, decimals: 2, suffix: "B+" };
  if (n >= 100_000_000) return { value: Math.floor(n / 1_000_000), decimals: 0, suffix: "M+" };
  if (n >= 1_000_000) return { value: n / 1_000_000, decimals: 1, suffix: "M+" };
  if (n >= 1_000) return { value: Math.floor(n / 1_000), decimals: 0, suffix: "K+" };
  return { value: n, decimals: 0, suffix: "+" };
};

export const Stats = () => {
  const { t } = useLang();
  const { channel } = useYouTube();
  const live = channel || {};
  const items = [
    { ...compact(live.total_views ?? CHANNEL_SNAPSHOT.totalViews), label: t.stats.labels.totalViews },
    { ...compact(live.subscribers ?? CHANNEL_SNAPSHOT.subscribers), label: t.stats.labels.subscribers },
    { ...compact(live.videos ?? CHANNEL_SNAPSHOT.videos), label: t.stats.labels.videos },
    { ...compact(live.top_recent_views ?? CHANNEL_SNAPSHOT.topRecentViews), label: t.stats.labels.topViews },
  ];

  return (
    <section data-testid="stats-section" className="border-y border-line bg-coal py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-ember">
            <span className="h-px w-8 bg-ember" aria-hidden="true" />
            {t.stats.eyebrow}
          </p>
        </Reveal>
        <div className="mt-14 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, i) => (
            <Reveal key={item.label} delay={i * 0.08}>
              <div className="border-t border-line pt-8">
                <Counter value={item.value} suffix={item.suffix} decimals={item.decimals} testId={`stat-value-${i}`} />
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.22em] text-ash">{item.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {t.stats.brands?.length > 0 && (
          <Reveal delay={0.15}>
            <div className="mt-20 border-t border-line pt-10" data-testid="brands-row">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ember">{t.stats.brandsLabel}</p>
              <div className="brand-strip mt-8 overflow-hidden" data-testid="brands-strip">
                <ul className="marquee-track flex w-max items-center">
                  {[0, 1].map((half) => (
                    <li key={half} className="flex items-center gap-x-16 pr-16 md:gap-x-24 md:pr-24" aria-hidden={half === 1}>
                      {t.stats.brands.map((brand, i) => (
                        <BrandLogo key={brand.name} brand={brand} index={half === 0 ? i : `dup-${i}`} />
                      ))}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
